import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Users, GraduationCap, Wallet, AlertTriangle, TrendingUp, CalendarDays, ArrowRight, MessageCircle,
} from "lucide-react";
import { api } from "@/lib/api";
import { rupiah, MONTHS } from "@/lib/format";
import { waLink, waMessages } from "@/lib/whatsapp";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/reports/summary").then((res) => setData(res.data)).catch(() => {});
  }, []);

  if (!data) {
    return <div className="py-20 text-center text-muted-foreground" data-testid="dashboard-loading">Memuat dashboard...</div>;
  }

  const kpis = [
    { label: "Siswa Aktif", value: data.students_active, icon: Users, testId: "kpi-students", color: "text-primary bg-primary/10" },
    { label: "Guru Aktif", value: data.teachers_count, icon: GraduationCap, testId: "kpi-teachers", color: "text-emerald-600 bg-emerald-50" },
    { label: "Pemasukan Bulan Ini", value: rupiah(data.income_month), icon: Wallet, testId: "kpi-income", color: "text-primary bg-primary/10" },
    { label: "Tunggakan SPP", value: `${data.arrears_count} siswa`, icon: AlertTriangle, testId: "kpi-arrears", color: "text-accent bg-accent/10" },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Dashboard</h1>
        <p className="mt-1 text-sm text-muted-foreground">Ringkasan operasional dan keuangan sekolah musik.</p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {kpis.map(({ label, value, icon: Icon, testId, color }) => (
          <Card key={label} className="border-border shadow-sm transition-shadow duration-200 hover:shadow-md" data-testid={testId}>
            <CardContent className="flex items-center gap-4 p-5">
              <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${color}`}>
                <Icon className="h-5 w-5" strokeWidth={2} />
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
                <p className="font-heading text-lg font-bold">{value}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="border-border shadow-sm lg:col-span-2" data-testid="dashboard-chart">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <TrendingUp className="h-5 w-5 text-primary" /> Pemasukan vs Pengeluaran (6 Bulan)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data.chart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${(v / 1000000).toFixed(1)}jt`} />
                <Tooltip formatter={(v) => rupiah(v)} />
                <Legend />
                <Bar dataKey="pemasukan" name="Pemasukan" fill="#1E3A8A" radius={[4, 4, 0, 0]} />
                <Bar dataKey="pengeluaran" name="Pengeluaran" fill="#F97316" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border-border shadow-sm" data-testid="dashboard-today-schedule">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <CalendarDays className="h-5 w-5 text-primary" /> Jadwal Hari Ini ({data.today_day})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.today_schedules.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">Tidak ada jadwal les hari ini.</p>
            ) : (
              <ul className="space-y-3">
                {data.today_schedules.map((s) => (
                  <li key={s.id} className="flex items-center justify-between rounded-lg border border-border p-3 transition-colors duration-200 hover:bg-slate-50">
                    <div>
                      <p className="text-sm font-semibold">{s.class_name}</p>
                      <p className="text-xs text-muted-foreground">{s.teacher_name} {s.room ? `• ${s.room}` : ""}</p>
                    </div>
                    <Badge variant="secondary">{s.start_time} - {s.end_time}</Badge>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="border-border shadow-sm lg:col-span-2" data-testid="dashboard-recent-payments">
          <CardHeader className="flex-row items-center justify-between">
            <CardTitle className="text-lg">Pembayaran SPP Terbaru</CardTitle>
            <Link to="/spp" className="group flex items-center gap-1 text-sm font-medium text-primary" data-testid="view-all-payments-link">
              Lihat semua <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </CardHeader>
          <CardContent>
            {data.recent_payments.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">Belum ada pembayaran.</p>
            ) : (
              <ul className="divide-y divide-border">
                {data.recent_payments.map((p) => (
                  <li key={p.id} className="flex items-center justify-between py-3">
                    <div>
                      <p className="text-sm font-semibold">{p.student_name}</p>
                      <p className="text-xs text-muted-foreground">{p.payment_type === "SPP" ? `SPP ${p.month_name} ${p.year}` : p.payment_type} • {p.method}</p>
                    </div>
                    <p className="text-sm font-bold text-primary">{rupiah(p.amount)}</p>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card className="border-border shadow-sm" data-testid="dashboard-arrears">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <AlertTriangle className="h-5 w-5 text-accent" /> Tunggakan Bulan Ini
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.arrears_list.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">Semua siswa sudah membayar. Bagus!</p>
            ) : (
              <ul className="space-y-3">
                {data.arrears_list.slice(0, 6).map((s) => (
                  <li key={s.id} className="flex items-center justify-between rounded-lg border border-accent/30 bg-accent/5 p-3">
                    <div>
                      <p className="text-sm font-semibold">{s.name}</p>
                      <p className="text-xs text-muted-foreground">{s.class_name} • {s.grade}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-bold text-accent">{rupiah(s.monthly_fee)}</p>
                      {s.phone && (
                        <a
                          href={waLink(s.phone, waMessages.reminder({ parentName: s.parent_name, studentName: s.name, className: s.class_name, grade: s.grade, monthName: MONTHS[new Date().getMonth()], year: new Date().getFullYear(), amount: s.monthly_fee }))}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`wa-arrears-${s.id}`}
                        >
                          <Button variant="ghost" size="icon" className="h-8 w-8" title="Ingatkan via WhatsApp">
                            <MessageCircle className="h-4 w-4 text-emerald-600" />
                          </Button>
                        </a>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
